<template>
  <div v-if="activeChips.length > 0 || lockedChips.length > 0" class="filter-chips" data-component="List/FilterChips">
    <!-- Chips verrouillées (filtres par défaut non suppressibles) -->
    <div
      v-for="chip in lockedChips"
      :key="chip.key"
      class="filter-chip filter-chip--locked"
      :title="chip.title"
    >
      <span class="filter-chip__label">{{ chip.label }}</span>
    </div>
    <!-- Chips actives (filtres utilisateur supprimables) -->
    <div
      v-for="chip in activeChips"
      :key="chip.key"
      class="filter-chip"
    >
      <span class="filter-chip__label">{{ chip.label }}</span>
      <button
        class="filter-chip__remove"
        type="button"
        :aria-label="`Retirer le filtre ${chip.label}`"
        @click="removeFilter(chip.key)"
      >
        <i class="ico close"></i>
      </button>
    </div>
  </div>
</template>

<script>
import dexieService from '@/services/dexieService'
import moment from 'moment'

/**
 * Mapping des clés de filtre vers leurs libellés affichés dans les chips
 */
const FILTER_LABELS = {
  nsil: 'NSIL',
  city: 'Ville',
  status: 'Occupation',
  hasPlanification: 'Planifié',
  hasNotPlanification: 'Non-planifié',
  hasHorsScaprim: 'Hors Scaprim',
  team: 'Agence',
  manager: 'Gestionnaire',
  assistance: 'Mandat cat',
  groupOwner: 'Mandat gpr',
  owner: 'Propriétaire',
  propertyType: 'Type',
  unit: 'Unité',
  subUnit: 'Sous-unité',
  startPlanificationAt: 'Planif. du',
  endPlanificationAt: 'Planif. au',
  startScheduledVisitAt: 'Visite du',
  endScheduledVisitAt: 'Visite au'
}

/**
 * Filtres à ne PAS afficher en chip (valeurs par défaut silencieuses).
 * Vide : tous les filtres actifs sont affichés car ils sont tous explicitement
 * définis par l'utilisateur (plus de DEFAULT_FILTERS automatiques dans le router).
 */
const HIDDEN_DEFAULTS = {}

/**
 * Valeurs humaines pour les champs enum
 */
const ENUM_VALUES = {
  status: {
    vacant: 'Vacant',
    'occupé': 'Occupé'
  }
}

export default {
  name: 'FilterChips',
  props: {
    query: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['remove-filter'],
  data() {
    return {
      // Tables de résolution des IDs vers des libellés
      teamsMap: {},
      managersMap: {},
      ownersMap: {},
      groupOwnersMap: {},
      assistancesMap: {},
      propertyTypesMap: {},
      unitsMap: {},
      subUnitsMap: {},
      referenceDataLoaded: false
    }
  },
  computed: {
    /**
     * Chips verrouillées représentant les filtres cachés appliqués par défaut.
     * Les libellés sont résolus dynamiquement depuis le dictionnaire property_type
     * afin d'afficher le vrai nom du type et la catégorie de filtre ("Type").
     *
     * Format : "Sans Type : [libellé du dictionnaire]"
     * Exemple : "Sans Type : Chambre Coliv" pour le code "$"
     */
    lockedChips() {
      return [
        {
          key: 'locked_excludeType_Y',
          label: `Sans Type : ${this.propertyTypesMap['Y'] || 'Y'}`,
          title: `Les lots de type "${this.propertyTypesMap['Y'] || 'Y'}" (code Y) sont toujours exclus`
        },
        {
          key: 'locked_excludeType_dollar',
          label: `Sans Type : ${this.propertyTypesMap['$'] || '$'}`,
          title: `Les lots de type "${this.propertyTypesMap['$'] || '$'}" (code $) sont toujours exclus`
        }
      ]
    },
    activeChips() {
      const chips = []

      Object.entries(this.query).forEach(([key, value]) => {
        // Ignorer les clés sans libellé défini et les valeurs vides
        if (!FILTER_LABELS[key] || !value || value === '') return

        // Ignorer les valeurs correspondant à des defaults masqués
        if (HIDDEN_DEFAULTS[key] && HIDDEN_DEFAULTS[key] === value) return

        const filterLabel = FILTER_LABELS[key]
        let valueLabel = ''

        // Champs booléens (checkbox) — pas de valeur affichée, juste le label
        if (key === 'hasPlanification' || key === 'hasNotPlanification' || key === 'hasHorsScaprim') {
          if (value === 'ok' || value === true) {
            chips.push({ key, label: filterLabel })
          }
          return
        }

        // Champs dateTime — formater la date (le champ se termine par 'At', ex: startPlanificationAt)
        if (key.endsWith('At')) {
          // Valider la date en mode strict ISO 8601 avant de la passer à moment()
          // pour éviter le warning "Non RFC2822/ISO date formats are discouraged"
          // qui survient si la valeur est une chaîne arbitraire (ex: 'today').
          const parsed = moment(value, moment.ISO_8601, true)
          valueLabel = parsed.isValid() ? parsed.format('DD/MM/YYYY') : value
          chips.push({ key, label: `${filterLabel} : ${valueLabel}` })
          return
        }

        // Champs enum avec valeurs prédéfinies
        if (ENUM_VALUES[key] && ENUM_VALUES[key][value]) {
          valueLabel = ENUM_VALUES[key][value]
          chips.push({ key, label: `${filterLabel} : ${valueLabel}` })
          return
        }

        // Champs avec résolution par ID depuis les tables de référence
        const resolvedLabel = this.resolveIdLabel(key, value)
        chips.push({ key, label: `${filterLabel} : ${resolvedLabel}` })
      })

      return chips
    }
  },
  mounted() {
    // Charger les données de référence au montage pour résoudre les libellés
    // des chips verrouillées (property_type Y et $), même si la query ne
    // contient pas encore de filtres basés sur des IDs.
    if (!this.referenceDataLoaded) {
      this.loadReferenceData()
    }
  },
  watch: {
    query: {
      handler(newQuery) {
        if (!this.referenceDataLoaded && this.hasIdBasedFilters(newQuery)) {
          this.loadReferenceData()
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    /**
     * Vérifie si la query contient des filtres basés sur des IDs
     * (qui nécessitent une résolution depuis IndexedDB)
     */
    hasIdBasedFilters(query) {
      const idKeys = ['team', 'manager', 'owner', 'groupOwner', 'assistance', 'propertyType', 'unit', 'subUnit']
      return idKeys.some(key => query[key] && query[key] !== '')
    },

    /**
     * Résout un ID en libellé humain selon la clé de filtre
     */
    resolveIdLabel(key, value) {
      switch (key) {
        case 'team':
          return this.teamsMap[value] || value
        case 'manager':
          return this.managersMap[value] || value
        case 'owner':
          return this.ownersMap[value] || value
        case 'groupOwner':
          return this.groupOwnersMap[value] || value
        case 'assistance':
          return this.assistancesMap[value] || value
        case 'propertyType':
          return this.propertyTypesMap[value] || value
        case 'unit':
          return this.unitsMap[value] || value
        case 'subUnit':
          return this.subUnitsMap[value] || value
        default:
          return String(value)
      }
    },

    /**
     * Charge les données de référence depuis IndexedDB pour résoudre les IDs
     */
    async loadReferenceData() {
      if (this.referenceDataLoaded) return

      try {
        // Charger en parallèle toutes les données de référence nécessaires
        const [teamsData, usersData, ownersData, groupOwnersData, assistancesData, propertyTypeDict, unitDict, subUnitDict] = await Promise.all([
          dexieService.getTeams().catch(() => []),
          dexieService.getUsers().catch(() => []),
          dexieService.db.table('legalOwners').toArray().catch(() => []),
          dexieService.db.table('managerMandators').toArray().catch(() => []),
          dexieService.db.table('assistanceMandators').toArray().catch(() => []),
          dexieService.getDictionary('property_type').catch(() => null),
          dexieService.getDictionary('unit').catch(() => null),
          dexieService.getDictionary('sub_unit').catch(() => null)
        ])

        // Construire les maps id → label
        teamsData.forEach(t => { this.teamsMap[t.id] = t.name })
        usersData.forEach(u => { this.managersMap[u.id] = u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() })
        ownersData.forEach(o => { this.ownersMap[o.id] = o.name })
        groupOwnersData.forEach(m => { this.groupOwnersMap[m.id] = m.name })
        assistancesData.forEach(a => { this.assistancesMap[a.id] = a.name })

        // Dictionnaires structurés
        if (propertyTypeDict) {
          const dict = propertyTypeDict.items ? propertyTypeDict.items : propertyTypeDict
          if (Array.isArray(dict)) {
            dict.forEach(item => { if (item.key) this.propertyTypesMap[item.key] = item.value })
          } else {
            Object.entries(dict).forEach(([k, v]) => { this.propertyTypesMap[k] = v })
          }
        }

        if (unitDict) {
          const dict = unitDict.items ? unitDict.items : unitDict
          if (Array.isArray(dict)) {
            dict.forEach(item => { if (item.key) this.unitsMap[item.key] = item.value })
          } else {
            Object.entries(dict).forEach(([k, v]) => { this.unitsMap[k] = v })
          }
        }

        if (subUnitDict) {
          const dict = subUnitDict.items ? subUnitDict.items : subUnitDict
          if (Array.isArray(dict)) {
            dict.forEach(item => { if (item.key) this.subUnitsMap[item.key] = item.value })
          } else {
            Object.entries(dict).forEach(([k, v]) => { this.subUnitsMap[k] = v })
          }
        }

        this.referenceDataLoaded = true
      } catch (error) {
        console.warn('[FilterChips] Erreur lors du chargement des données de référence:', error)
      }
    },

    /**
     * Émet l'événement de suppression d'un filtre
     */
    removeFilter(key) {
      this.$emit('remove-filter', key)
    }
  }
}
</script>

<style lang="scss" scoped>
@use "../../assets/styles/variables" as *;

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  padding: 0 10px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #e8f0fe;
  border: 1px solid #4a80d4;
  border-radius: 100px;
  padding: 2px 6px 2px 10px;
  font-size: 0.78rem;
  color: #2c5ba8;
  white-space: nowrap;
  max-width: 200px;

  &__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__remove {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 1px;
    color: #2c5ba8;
    opacity: 0.7;
    flex-shrink: 0;

    &:hover {
      opacity: 1;
    }

    .ico.close {
      width: 9px;
      height: 9px;
      font-size: 9px;
    }
  }

  // Variante verrouillée : filtre par défaut non suppressible
  &--locked {
    background-color: #f0f0f0;
    border-color: #b0b0b0;
    color: #888;
    cursor: default;
    padding: 2px 10px 2px 8px;
    user-select: none;

    &::before {
      content: "🔒";
      font-size: 8px;
      opacity: 0.7;
      flex-shrink: 0;
    }

    &:hover {
      background-color: #f0f0f0;
    }
  }

}
</style>
